import type { ResultOf } from 'gql.tada';

import { graphql } from '@eluve/graphql.tada';

// Fragment for the user data in prompt template defaults
export const promptTemplateDefaultUserFragment = graphql(`
  fragment PromptTemplateDefaultUser on Users @_unmask {
    __typename
    id
    email
    firstName
    lastName
  }
`);

// Fragment for the tenant data
export const promptTemplateDefaultTenantFragment = graphql(`
  fragment PromptTemplateDefaultTenant on Tenants @_unmask {
    __typename
    id
    name
  }
`);

// Fragment for the prompt template reference
export const promptTemplateReferenceFragment = graphql(`
  fragment PromptTemplateReference on PromptTemplates @_unmask {
    __typename
    id
    name
    outputType
    isActive
    prompt_template_variants(
      where: { isCurrentDefault: { _eq: true } }
      limit: 1
    ) {
      __typename
      promptTemplateId
      variantNumber
      template
    }
  }
`);

export type PromptTemplateReference = ResultOf<
  typeof promptTemplateReferenceFragment
>;

// Main fragment for prompt_template_defaults
export const promptTemplateDefaultFragment = graphql(
  `
    fragment PromptTemplateDefault on PromptTemplateDefaults @_unmask {
      __typename
      id
      tenantId
      userId
      llmOutputType
      specialty
      language
      promptTemplateId
      outputTemplateId
      customInstructions
      createdAt
      updatedAt
      tenant {
        ...PromptTemplateDefaultTenant
      }
      user {
        ...PromptTemplateDefaultUser
      }
      prompt_template {
        ...PromptTemplateReference
      }
      output_template {
        __typename
        id
        name
        llmOutputType
      }
    }
  `,
  [
    promptTemplateDefaultTenantFragment,
    promptTemplateDefaultUserFragment,
    promptTemplateReferenceFragment,
  ],
);

// Fragment for llm output types
export const llmOutputTypesListFragment = graphql(`
  fragment LlmOutputTypesList on query_root @_unmask {
    llmOutputTypes {
      __typename
      name
      description
    }
  }
`);

// Fragment for prompt templates list (for selection)
export const promptTemplatesListFragment = graphql(
  `
    fragment PromptTemplatesListForDefaults on query_root @_unmask {
      promptTemplates(
        where: { isActive: { _eq: true } }
        orderBy: { name: ASC }
      ) {
        ...PromptTemplateReference
      }
    }
  `,
  [promptTemplateReferenceFragment],
);

// Mutation to create a new prompt template default
export const insertPromptTemplateDefaultMutation = graphql(
  `
    mutation insertPromptTemplateDefault(
      $tenantId: uuid
      $userId: uuid
      $llmOutputType: LlmOutputTypesEnum!
      $specialty: citext
      $language: String
      $promptTemplateId: uuid!
      $outputTemplateId: uuid
      $customInstructions: String
    ) {
      insertPromptTemplateDefaultsOne(
        object: {
          tenantId: $tenantId
          userId: $userId
          llmOutputType: $llmOutputType
          specialty: $specialty
          language: $language
          promptTemplateId: $promptTemplateId
          outputTemplateId: $outputTemplateId
          customInstructions: $customInstructions
        }
      ) {
        ...PromptTemplateDefault
      }
    }
  `,
  [promptTemplateDefaultFragment],
);

// Mutation to update an existing prompt template default
export const updatePromptTemplateDefaultMutation = graphql(
  `
    mutation updatePromptTemplateDefault(
      $id: uuid!
      $tenantId: uuid
      $userId: uuid
      $llmOutputType: LlmOutputTypesEnum!
      $specialty: citext
      $language: String
      $promptTemplateId: uuid!
      $outputTemplateId: uuid
      $customInstructions: String
    ) {
      updatePromptTemplateDefaultsByPk(
        pkColumns: { id: $id }
        _set: {
          tenantId: $tenantId
          userId: $userId
          llmOutputType: $llmOutputType
          specialty: $specialty
          language: $language
          promptTemplateId: $promptTemplateId
          outputTemplateId: $outputTemplateId
          customInstructions: $customInstructions
        }
      ) {
        ...PromptTemplateDefault
      }
    }
  `,
  [promptTemplateDefaultFragment],
);

// Mutation to delete a prompt template default
export const deletePromptTemplateDefaultMutation = graphql(`
  mutation deletePromptTemplateDefault($id: uuid!) {
    deletePromptTemplateDefaultsByPk(id: $id) {
      __typename
      id
    }
  }
`);

// Query to get prompt template defaults for a specific tenant (tenant-scoped + user overrides)
export const getTenantPromptTemplateDefaultsQuery = graphql(
  `
    query getTenantPromptTemplateDefaults($tenantId: uuid!) {
      # Tenant-level defaults
      tenantDefaults: promptTemplateDefaults(
        where: { tenantId: { _eq: $tenantId }, userId: { _isNull: true } }
        orderBy: { llmOutputType: ASC, createdAt: ASC }
      ) {
        ...PromptTemplateDefault
      }
      # User-level overrides within the tenant
      userDefaults: promptTemplateDefaults(
        where: { tenantId: { _eq: $tenantId }, userId: { _isNull: false } }
        orderBy: { llmOutputType: ASC, createdAt: ASC }
      ) {
        ...PromptTemplateDefault
      }
      # Distinct specialties for users in this tenant
      tenantSpecialties: tenantUsers(
        where: {
          tenantId: { _eq: $tenantId }
          user: { specialty: { _isNull: false } }
        }
        distinctOn: [userId]
      ) {
        __typename
        tenantId
        userId
        user {
          __typename
          id
          specialty
        }
      }
      # Tenant-enabled LLM output types and prompt templates
      tenantsByPk(id: $tenantId) {
        __typename
        id
        # Enabled LLM output types for this tenant
        tenant_llm_outputs(where: { isEnabled: { _eq: true } }) {
          __typename
          tenantId
          llmOutputName
          isEnabled
        }
        # Enabled prompt templates for this tenant
        promptTemplates(where: { isEnabled: { _eq: true } }) {
          __typename
          tenantId
          promptTemplateId
          isEnabled
          prompt_template {
            ...PromptTemplateReference
          }
        }
        # Enabled output templates for this tenant
        llmOutputTemplates(where: { isEnabled: { _eq: true } }) {
          __typename
          tenantId
          llmOutputTemplateId
          llm_output_template {
            __typename
            id
            name
            llmOutputType
          }
        }
      }
      # Keep global lists for fallback/reference
      ...LlmOutputTypesList
      ...PromptTemplatesListForDefaults
    }
  `,
  [
    promptTemplateDefaultFragment,
    promptTemplateReferenceFragment,
    llmOutputTypesListFragment,
    promptTemplatesListFragment,
  ],
);

// Query to get tenant users for override management
export const getTenantUsersForOverridesQuery = graphql(
  `
    query getTenantUsersForOverrides($tenantId: uuid!) {
      tenantsByPk(id: $tenantId) {
        __typename
        id
        name
        tenantUsers(orderBy: { user: { firstName: ASC } }) {
          __typename
          tenantId
          userId
          role
          user {
            ...PromptTemplateDefaultUser
            promptTemplateDefaultsAggregate(
              where: { tenantId: { _eq: $tenantId } }
            ) {
              aggregate {
                __typename
                count
              }
            }
          }
        }
      }
    }
  `,
  [promptTemplateDefaultUserFragment],
);

// Query to get user-specific defaults within a tenant
export const getUserPromptTemplateDefaultsQuery = graphql(
  `
    query getUserPromptTemplateDefaults($tenantId: uuid!, $userId: uuid!) {
      promptTemplateDefaults(
        where: { tenantId: { _eq: $tenantId }, userId: { _eq: $userId } }
        orderBy: { llmOutputType: ASC, createdAt: ASC }
      ) {
        ...PromptTemplateDefault
      }
      ...LlmOutputTypesList
      ...PromptTemplatesListForDefaults
    }
  `,
  [
    promptTemplateDefaultFragment,
    llmOutputTypesListFragment,
    promptTemplatesListFragment,
  ],
);

// Query to get a user's personal settings from tenant_user_settings
export const getUserPersonalSettingsQuery = graphql(`
  query getUserPersonalSettings($tenantId: uuid!, $userId: uuid!) {
    tenantUserSettingsByPk(tenantId: $tenantId, userId: $userId) {
      __typename
      tenantId
      userId
      promptTemplateId
      llmOutputTemplateId
    }
  }
`);

// Mutation for admin to update a user's personal template preferences
export const updateUserPersonalSettingsMutation = graphql(`
  mutation updateUserPersonalSettings(
    $tenantId: uuid!
    $userId: uuid!
    $promptTemplateId: uuid
    $llmOutputTemplateId: uuid
  ) {
    updateTenantUserSettingsByPk(
      pkColumns: { tenantId: $tenantId, userId: $userId }
      _set: {
        promptTemplateId: $promptTemplateId
        llmOutputTemplateId: $llmOutputTemplateId
      }
    ) {
      __typename
      tenantId
      userId
      promptTemplateId
      llmOutputTemplateId
    }
  }
`);

// Query to get global defaults (no tenant, no user)
export const getGlobalPromptTemplateDefaultsQuery = graphql(
  `
    query getGlobalPromptTemplateDefaults {
      globalDefaults: promptTemplateDefaults(
        where: { tenantId: { _isNull: true }, userId: { _isNull: true } }
        orderBy: { llmOutputType: ASC, createdAt: ASC }
      ) {
        ...PromptTemplateDefault
      }
      # All active output templates (for global scope)
      llmOutputTemplates(
        where: { isActive: { _eq: true } }
        orderBy: { name: ASC }
      ) {
        __typename
        id
        name
        llmOutputType
      }
      ...LlmOutputTypesList
      ...PromptTemplatesListForDefaults
    }
  `,
  [
    promptTemplateDefaultFragment,
    llmOutputTypesListFragment,
    promptTemplatesListFragment,
  ],
);
